<template>
  <div>
    <v-dialog v-model="addCurriculumDialog" persistent width="70%">
      <v-card>
        <v-toolbar color="#EEF2F6">
          <v-card-title>
            <i class="fal fa-books mr-2"></i>
            <span class="headline">Add Curriculum</span>
          </v-card-title>
          <v-spacer></v-spacer>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm mr-2"
                v-bind="attrs"
                v-on="on"
                readonly=""
                @click="toggleHelperDialog"
              >
                <i class="fas fa-question" />
              </button>
            </template>
            <span>More information</span>
          </v-tooltip>
          <button
            type="button"
            class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm"
            @click="close"
            :disabled="isLoading"
          >
            <i class="fal fa-times"></i>
          </button>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-text-field
                  v-model="curriculum.name"
                  v-validate="'required'"
                  label="Curriculum Name"
                  name="name"
                  :disabled="isLoading"
                  :loading="isLoading"
                />
                <span
                  class="kt-font-danger validate-error"
                  v-show="errors.has('name')"
                  >{{ errors.first('name') }}</span
                >
              </v-col>
              <v-col v-if="!selfAssign" cols="12" sm="12" md="12">
                <v-autocomplete
                  v-model="curriculum.assignedDealer"
                  :menu-props="{ closeOnClick: true }"
                  :disabled="isLoading"
                  :items="dealers"
                  chips
                  hide-selected
                  label="Assign to Company"
                  item-text="name"
                  item-value="id"
                  multiple
                  name="assigned_dealers"
                  @change="debounceFetching"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="data.select"
                      @click:close="removeDealer(data.item)"
                    >
                      {{ data.item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col v-if="!selfAssign" cols="12" sm="12" md="12">
                <v-autocomplete
                  v-model="curriculum.assignedSpecificDealers"
                  :menu-props="{ closeOnClick: true }"
                  :disabled="fetchingSpecificDealers"
                  :loading="fetchingSpecificDealers"
                  :items="specific_dealers"
                  chips
                  hide-selected
                  label="Assign to Sub Company"
                  item-text="name"
                  item-value="id"
                  multiple
                  name="assigned_specific_dealers"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="data.select"
                      @click:close="removeSpecificDealer(data.item)"
                    >
                      <v-list-item-content>
                        <v-list-item-title
                          v-html="data.item.name"
                        ></v-list-item-title>
                        <v-list-item-subtitle
                          v-html="data.item.dealer.name"
                        ></v-list-item-subtitle>
                      </v-list-item-content>
                    </v-chip>
                  </template>
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title
                        v-html="data.item.name"
                      ></v-list-item-title>
                      <v-list-item-subtitle
                        v-html="data.item.dealer.name"
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <span
                  class="kt-font-danger validate-error"
                  v-show="errors.has('assigned_specific_dealers')"
                  >{{ errors.first('assigned_specific_dealers') }}</span
                >
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <v-switch
                  v-model="curriculum.is_draft"
                  :label="`Draft`"
                  :disabled="isLoading"
                ></v-switch>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue-grey darken-1"
            text
            :disabled="isLoading"
            :loading="isLoading"
            @click="reset"
            >Reset</v-btn
          >
          <v-btn
            color="blue-grey darken-1"
            text
            @click="close"
            :disabled="isLoading"
            :loading="isLoading"
            >Cancel</v-btn
          >
          <v-btn
            color="blue darken-1"
            text
            @click="store"
            :disabled="isLoading"
            :loading="isLoading"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <helper-dialog
      :close-dialog="closeConfirmDialog"
      :show-dialog="showHelperDialog"
    >
      <ul>
        <li><strong>Curriculum</strong> refers to the set of courses.</li>
        <li>
          <strong>Assign Delear</strong> refers to the set of companies you want
          to show the curriculum.
        </li>
        <li>
          <strong>Draft</strong> refers to the status if you want your
          curriculum to be displayed on your homepage.
        </li>
      </ul>
    </helper-dialog>
  </div>
</template>

<script>
import {
  map as _map,
  head as _head,
  sortBy as _sortBy,
  some as _some,
} from 'lodash-es';
import WordEditor from '../../WordEditor.vue';
import HelperDialog from '@/components/dialogs/HelperDialog';
export default {
  name: 'AddCurriculumDialog',
  components: {
    WordEditor,
    HelperDialog,
  },
  props: {
    addCurriculumDialog: Boolean,
    closeModal: Function,
    toggleAddCurriculumDialog: Function,
    selfAssign: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      curriculum: {
        name: null,
        assignedDealer: null,
        assignedSpecificDealers: null,
        is_draft: false,
      },
      fetchingSpecificDealers: false,
      dealers: [],
      specific_dealers: [],
      isLoading: false,
      showSpecificDealers: false,
      debounceTime: null,
      showHelperDialog: false,
    };
  },
  watch: {
    addCurriculumDialog(val) {
      val || this.close();
    },
  },
  computed: {
    user() {
      return this.$auth.user();
    },

    isAdmin() {
      return _some(this.$auth.user().roles, { name: 'super-administrator' });
    },

    isManager() {
      return _some(this.$auth.user().roles, { name: 'account-manager' });
    },
  },
  mounted() {
    this.fetchDealers();
  },
  methods: {
    reset() {
      this.errors.clear();
      this.$validator.reset();
      this.curriculum.name = null;
      this.curriculum.assignedDealer = [];
      this.curriculum.assignedSpecificDealers = [];
      this.curriculum.is_draft = false;
      this.showSpecificDealers = false;
    },
    close() {
      this.reset();
      this.closeModal();
    },
    store() {
      this.isLoading = true;
      this.$validator.validate('*').then((result) => {
        if (result) {
          this.$http
            .post('/lms-manager/courses', {
              name: this.curriculum.name,
              assigned_dealers: this.curriculum.assignedDealer,
              assigned_specific_dealers:
                this.curriculum.assignedSpecificDealers,
              is_draft: this.curriculum.is_draft,
            })
            .then(({ data }) => {
              this.$emit('curriculum', data);
              this.$notify('success', 'Curriculum added!');
            })
            .catch((errors) => {
              this.interactWithErrorBag(
                errors.response.data.errors,
                this.errors
              );
            })
            .finally(() => {
              this.isLoading = false;
              this.close();
            });
        } else {
          this.isLoading = false;
        }
      });
    },
    fetchDealers() {
      this.isLoading = true;
      this.axios
        .get('client/lms/fetchMainCompanies')
        .then((data) => {
          this.dealers = data.data;
          if (!this.isAdmin) {
            const pushCurrentDealer = [this.user.dealer_id];
            this.curriculum.assignedDealer = pushCurrentDealer;
            this.fetchSpecificDealers();
          }
          this.isLoading = false;
        })
        .finally((data) => {
          this.isLoading = false;
        });
    },

    fetchSpecificDealers() {
      this.fetchingSpecificDealers = true;

      if (this.curriculum.assignedDealer.length == 0) {
        this.fetchingSpecificDealers = false;
        this.showSpecificDealers = false;
      } else {
        this.axios
          .get('client/lms/fetchSubCompanies', {
            params: {
              assigned_dealers: this.curriculum.assignedDealer,
            },
          })
          .then((data) => {
            this.specific_dealers = data.data;
            this.fetchingSpecificDealers = false;
            if (data.data.length !== 0) {
              this.showSpecificDealers = true;
            }
          })
          .finally((data) => {
            this.fetchingSpecificDealers = false;
          });
      }
    },

    debounceFetching() {
      clearTimeout(this.debounceTime);

      this.debounceTime = setTimeout(() => {
        this.fetchSpecificDealers();
      }, 1000);
    },

    removeDealer(item) {
      const index = this.curriculum.assignedDealer.indexOf(item.id);
      if (index >= 0) this.curriculum.assignedDealer.splice(index, 1);
      this.debounceFetching();
    },

    removeSpecificDealer(item) {
      const index = this.curriculum.assignedSpecificDealers.indexOf(item.id);
      if (index >= 0) this.curriculum.assignedSpecificDealers.splice(index, 1);
    },
    toggleHelperDialog() {
      this.showHelperDialog = true;
    },
    closeConfirmDialog() {
      this.showHelperDialog = false;
    },
  },
};
</script>

<style scoped></style>
